import { z } from 'zod';
import { dateTimeSchema, idSchema, nullableDateTimeSchema } from './common';
import { deliveryFormatSchema, modelIdSchema } from './enums';

/**
 * SPEC §8 — the single Settings JSON row.
 * Every numeric field is bounded so the UI can render it as a slider;
 * free-text config fields are forbidden.
 */

export const HARD_MAX_CONCURRENCY = 8;

export const voiceProfileSchema = z.object({
  /** "" until a SUPER_ADMIN picks a voice; generation returns 422 while empty. */
  voiceId: z.string().max(64).default(''),
  modelId: modelIdSchema.default('eleven_multilingual_v2'),
  stability: z.number().min(0).max(1).default(0.7),
  similarityBoost: z.number().min(0).max(1).default(0.8),
  style: z.number().min(0).max(1).default(0),
  useSpeakerBoost: z.boolean().default(true),
  speed: z.number().min(0.7).max(1.2).default(1),
});
export type VoiceProfile = z.infer<typeof voiceProfileSchema>;

export const voicesSettingsSchema = z.object({
  en: voiceProfileSchema.extend({ speed: z.number().min(0.7).max(1.2).default(1) }).default({}),
  ar: voiceProfileSchema.extend({ speed: z.number().min(0.7).max(1.2).default(0.95) }).default({}),
});
export type VoicesSettings = z.infer<typeof voicesSettingsSchema>;

export const generationSettingsSchema = z.object({
  concurrency: z.number().int().min(1).max(HARD_MAX_CONCURRENCY).default(4),
  maxRowsPerImport: z.number().int().min(1).max(5000).default(500),
  maxCellChars: z.number().int().min(1).max(5000).default(1000),
  maxAttempts: z.number().int().min(1).max(10).default(3),
});
export type GenerationSettings = z.infer<typeof generationSettingsSchema>;

export const publishingSettingsSchema = z.object({
  deliveryFormat: deliveryFormatSchema.default('mp3'),
  processAudio: z.boolean().default(false),
  loudnessTargetLufs: z.number().min(-40).max(-5).default(-16),
  silenceThresholdDb: z.number().min(-90).max(-10).default(-50),
  filenameWordCount: z.number().int().min(1).max(10).default(4),
  /** Constrained shape (not free text) — the UI offers a max-age slider. */
  cacheControl: z
    .string()
    .regex(/^(public|private), max-age=\d{1,7}$/, 'Expected e.g. "public, max-age=300"')
    .default('public, max-age=300'),
});
export type PublishingSettings = z.infer<typeof publishingSettingsSchema>;

export const settingsDataSchema = z.object({
  voices: voicesSettingsSchema.default({}),
  generation: generationSettingsSchema.default({}),
  publishing: publishingSettingsSchema.default({}),
});
export type SettingsData = z.infer<typeof settingsDataSchema>;

/** The seeded defaults (voiceId "" for both languages, SPEC §8). */
export const DEFAULT_SETTINGS: SettingsData = settingsDataSchema.parse({});

/** GET /api/settings */
export const settingsResponseSchema = z.object({
  data: settingsDataSchema,
  updatedAt: dateTimeSchema,
  updatedById: idSchema.nullable(),
  updatedByName: z.string().nullable(),
});
export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

/** PUT /api/settings — partial patch, deep-merged onto the stored document then re-validated. */
export const updateSettingsRequestSchema = z.object({
  voices: z
    .object({
      en: voiceProfileSchema.partial().optional(),
      ar: voiceProfileSchema.partial().optional(),
    })
    .optional(),
  generation: generationSettingsSchema.partial().optional(),
  publishing: publishingSettingsSchema.partial().optional(),
});
export type UpdateSettingsRequest = z.infer<typeof updateSettingsRequestSchema>;

export const updateSettingsResponseSchema = settingsResponseSchema;
export type UpdateSettingsResponse = z.infer<typeof updateSettingsResponseSchema>;

/** GET /api/settings/status — booleans only, never secret values (SPEC §8). */
export const settingsStatusResponseSchema = z.object({
  elevenLabs: z.object({
    apiKeyConfigured: z.boolean(),
    planTier: z.string(),
    safeConcurrency: z.number().int().min(1),
  }),
  gcs: z.object({
    credentialsConfigured: z.boolean(),
    bucketConfigured: z.boolean(),
    bucket: z.string(),
    prefix: z.string(),
    publicUrlPrefix: z.string(),
  }),
  voices: z.object({
    enSelected: z.boolean(),
    arSelected: z.boolean(),
  }),
  worker: z.object({
    enabled: z.boolean(),
    concurrency: z.number().int().min(1),
  }),
  /** false while either voiceId is "" — generation is blocked until true. */
  readyToGenerate: z.boolean(),
});
export type SettingsStatusResponse = z.infer<typeof settingsStatusResponseSchema>;

/** POST /api/settings/test-elevenlabs (GET /v1/user → tier + remaining credits). */
export const testElevenLabsResponseSchema = z.object({
  ok: z.boolean(),
  tier: z.string().nullable(),
  characterCount: z.number().int().nullable(),
  characterLimit: z.number().int().nullable(),
  remainingCredits: z.number().int().nullable(),
  nextResetAt: nullableDateTimeSchema,
  latencyMs: z.number().int().min(0),
  error: z.string().nullable(),
});
export type TestElevenLabsResponse = z.infer<typeof testElevenLabsResponseSchema>;

/** POST /api/settings/test-gcs (write + delete `{prefix}/healthcheck/{ts}.txt`). */
export const testGcsResponseSchema = z.object({
  ok: z.boolean(),
  bucket: z.string(),
  path: z.string().nullable(),
  latencyMs: z.number().int().min(0),
  error: z.string().nullable(),
});
export type TestGcsResponse = z.infer<typeof testGcsResponseSchema>;

/** GET /api/settings/models — proxied /v1/models, filtered by the server-side allowlist. */
export const elevenLabsModelSchema = z.object({
  modelId: modelIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  canDoTextToSpeech: z.boolean(),
  maxCharactersRequest: z.number().int().nullable(),
  languages: z.array(z.object({ languageId: z.string(), name: z.string() })),
});
export type ElevenLabsModel = z.infer<typeof elevenLabsModelSchema>;

export const listModelsResponseSchema = z.object({ models: z.array(elevenLabsModelSchema) });
export type ListModelsResponse = z.infer<typeof listModelsResponseSchema>;

/** GET /api/settings/voices — proxied /v1/voices with playable preview URLs. */
export const elevenLabsVoiceSchema = z.object({
  voiceId: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  description: z.string().nullable(),
  previewUrl: z.string().nullable(),
  labels: z.record(z.string()),
});
export type ElevenLabsVoice = z.infer<typeof elevenLabsVoiceSchema>;

export const listVoicesResponseSchema = z.object({ voices: z.array(elevenLabsVoiceSchema) });
export type ListVoicesResponse = z.infer<typeof listVoicesResponseSchema>;
